import React from 'react';
import { Modal, Button, Alert, Typography, Space } from 'antd';
import { UserStatusChip } from './UserStatusChip';

const { Text, Paragraph } = Typography;

interface UserStatusDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  currentStatus: 'active' | 'inactive' | 'pending' | string;
  newStatus: 'active' | 'inactive' | 'pending' | string;
  userName: string;
  loading?: boolean;
  error?: string | null;
}

const getStatusMessage = (
  currentStatus: string,
  newStatus: string,
  userName: string
): { title: string; description: string } => {
  const statusLabels = {
    active: 'actif',
    inactive: 'inactif',
    pending: 'en attente',
  };

  const current = statusLabels[currentStatus as keyof typeof statusLabels] || currentStatus;
  const next = statusLabels[newStatus as keyof typeof statusLabels] || newStatus;

  return {
    title: `Changer le statut de ${userName}`,
    description: `
      Vous êtes sur le point de passer le statut de cet utilisateur de 
      "${current}" à "${next}". 
      ${newStatus === 'inactive' 
        ? 'L\'utilisateur ne pourra plus se connecter tant que le compte est inactif.' 
        : newStatus === 'pending'
          ? 'Un email sera envoyé à l\'utilisateur pour finaliser son inscription.'
          : 'L\'utilisateur aura à nouveau accès à son compte.'
      }
    `,
  };
};

export const UserStatusDialog: React.FC<UserStatusDialogProps> = ({
  open,
  onClose,
  onConfirm,
  currentStatus,
  newStatus,
  userName,
  loading = false,
  error = null,
}) => {
  const { title, description } = getStatusMessage(currentStatus, newStatus, userName);

  return (
    <Modal
      open={open}
      onCancel={!loading ? onClose : undefined}
      title={
        <Space style={{ width: '100%', justifyContent: 'space-between' }}>
          <span>{title}</span>
          <Space>
            <UserStatusChip status={currentStatus} />
            <span>→</span>
            <UserStatusChip status={newStatus} />
          </Space>
        </Space>
      }
      footer={[
        <Button key="cancel" onClick={onClose} disabled={loading}>
          Annuler
        </Button>,
        <Button
          key="confirm"
          type="primary"
          onClick={onConfirm}
          loading={loading}
        >
          {loading ? 'Traitement...' : 'Confirmer'}
        </Button>,
      ]}
      closable={!loading}
      width={600}
    >
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        {error && (
          <Alert
            message={error}
            type="error"
            showIcon
          />
        )}
        
        {description.split('\n').map((paragraph, index) => (
          <Paragraph key={index}>
            {paragraph.trim()}
          </Paragraph>
        ))}
        
        <Text type="secondary" italic>
          Cette action peut être annulée à tout moment.
        </Text>
      </Space>
    </Modal>
  );
};

export default UserStatusDialog;
